// src/redux/slices/creditsSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CreditApi } from "~/functions/api/credit";
import type { Credit } from "~/interface/credit";
import type { RootState } from "~/store";

const creditApi = new CreditApi();

interface CreditsState {
  totalCount: number;
  currentList: Credit[];
  list: Credit[];
  loading: boolean;
  error: string | null;
  limit: number;
  page: number;
}

const initialState: CreditsState = {
  totalCount: 0,
  currentList: [],
  list: [],
  loading: false,
  error: null,
  page: 1,
  limit: 10,
};

// 非同期アクションを定義
export const loadCredits = createAsyncThunk(
  "credits/loadCredits",
  async (params: { page: number; limit: number }, { getState }) => {
    const state = getState() as RootState;
    const organizationId = state.auth.currentUser.organization_id;
    const data = await creditApi.getPaginatedCreditsList(organizationId, params.limit, params.page);
    return data;
  },
);

export const loadCurrentCredits = createAsyncThunk(
  "credits/loadCurrentCredits",
  async (_, { getState }) => {
    const state = getState() as RootState;
    const organizationId = state.auth.currentUser.organization_id;
    const data = await creditApi.getPaginatedCreditsList(organizationId, 3, 1);
    return data;
  },
);

const creditsSlice = createSlice({
  name: "credits",
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCredits.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload ? action.payload.credits : [];
        state.totalCount = action.payload ? action.payload.total_count : 0;
      })
      .addCase(loadCredits.rejected, (state, action) => {
        state.loading = false;
        console.error(action.error.message);
        state.error = "クレジット履歴一覧の取得に失敗しました。";
      })
      .addCase(loadCurrentCredits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCurrentCredits.fulfilled, (state, action) => {
        state.loading = false;
        state.currentList = action.payload ? action.payload.credits : [];
      })
      .addCase(loadCurrentCredits.rejected, (state, action) => {
        state.loading = false;
        console.error(action.error.message);
        state.error = "最近のクレジット履歴の取得に失敗しました。";
      });
  },
});

export const { setPage } = creditsSlice.actions;
export default creditsSlice.reducer;
